import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const Benefits = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language];
  const isROPage = location.pathname === '/ro';
  
  const benefits = isROPage ? [
    {
      number: '01',
      title: t.roBenefit1Title,
      description: t.roBenefit1Desc,
      icon: '💧'
    },
    {
      number: '02',
      title: t.roBenefit2Title,
      description: t.roBenefit2Desc,
      icon: '✨'
    },
    {
      number: '03',
      title: t.roBenefit3Title,
      description: t.roBenefit3Desc,
      icon: '🏥'
    },
    {
      number: '04',
      title: t.roBenefit4Title,
      description: t.roBenefit4Desc,
      icon: '💰'
    },
    {
      number: '05',
      title: t.roBenefit5Title,
      description: t.roBenefit5Desc,
      icon: '🚰'
    },
    {
      number: '06',
      title: t.roBenefit6Title,
      description: t.roBenefit6Desc,
      icon: '⚖️'
    }
  ] : [
    {
      number: '01',
      title: t.benefit1Title,
      description: t.benefit1Desc,
      icon: '🛡️'
    },
    {
      number: '02',
      title: t.benefit2Title,
      description: t.benefit2Desc,
      icon: '🚫'
    },
    {
      number: '03',
      title: t.benefit3Title,
      description: t.benefit3Desc,
      icon: '📸'
    },
    {
      number: '04',
      title: t.benefit4Title,
      description: t.benefit4Desc,
      icon: '📱'
    },
    {
      number: '05',
      title: t.benefit5Title,
      description: t.benefit5Desc,
      icon: '💰'
    },
    {
      number: '06',
      title: t.benefit6Title,
      description: t.benefit6Desc,
      icon: '👨‍🔧'
    }
  ];

  const borderColor = isROPage ? 'border-blue-500/50' : 'border-green-500/50';
  const textColor = isROPage ? 'text-blue-400' : 'text-green-400';
  const gradientColor = isROPage ? 'from-blue-400 to-cyan-400' : 'from-green-400 to-cyan-400';
  const gradientLine = isROPage ? 'from-blue-500 to-cyan-500' : 'from-green-500 to-cyan-500';
  const bgGradient = isROPage ? 'bg-gradient-to-b from-white via-blue-50 to-cyan-50' : 'bg-gradient-to-b from-black via-gray-900 to-black';
  const textMain = isROPage ? 'text-gray-900' : 'text-white';
  const textSecondary = isROPage ? 'text-gray-600' : 'text-gray-400';
  const cardBg = isROPage ? 'bg-white shadow-xl' : 'bg-gray-900/80 backdrop-blur-sm';
  const badgeGradient = isROPage ? 'from-blue-500 to-cyan-500' : 'from-green-500 to-cyan-500';

  return (
    <section className={`py-24 ${bgGradient} relative overflow-hidden ${isROPage ? 'text-gray-900' : 'text-white'}`}>
      {/* Background Pattern */}
      {!isROPage && (
        <div className="absolute inset-0 opacity-5">
          <div className="grid grid-cols-6 gap-2 h-full p-4">
            {[...Array(18)].map((_, i) => (
              <div key={i} className="bg-gray-800 border border-green-500/10 rounded relative overflow-hidden animate-flicker" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="absolute top-1 left-1 flex items-center space-x-1">
                  <div className="w-1 h-1 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-[8px] text-green-400 font-mono">REC</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {isROPage && (
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 water-wave"></div>
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className={`inline-block px-6 py-2 ${isROPage ? 'bg-blue-100 border-2 border-blue-400' : 'bg-black/60 border-2 border-green-500/50'} rounded mb-6`}>
            <span className={`${isROPage ? 'text-blue-700' : textColor} font-semibold font-mono text-sm tracking-wider`}>
              {isROPage ? t.roBenefitsBadge : t.benefitsBadge}
            </span>
          </div>
          <h2 className={`text-5xl md:text-6xl font-extrabold mb-6 font-mono ${textMain}`}>
            {isROPage ? t.roBenefitsTitle : t.benefitsTitle} <span className={`bg-gradient-to-r ${gradientColor} bg-clip-text text-transparent`}>
              {isROPage ? t.roBenefitsTitle2 : t.benefitsTitle2}
            </span>
          </h2>
          <p className={`text-xl md:text-2xl ${textSecondary} max-w-3xl mx-auto font-mono`}>
            {isROPage ? t.roBenefitsSubtitle : t.benefitsSubtitle}
          </p>
          <div className={`w-24 h-1 bg-gradient-to-r ${gradientLine} mx-auto mt-6 rounded-full`}></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`group relative ${cardBg} rounded-xl p-8 border-2 ${borderColor} hover:${isROPage ? 'border-blue-500/80' : 'border-green-500/60'} transition-all duration-500 transform hover:-translate-y-2 animate-slide-in-left`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Number Badge */}
              <div className={`absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br ${badgeGradient} rounded-full flex items-center justify-center shadow-lg`}>
                <span className="text-2xl font-bold text-white font-mono">{benefit.number}</span>
              </div>

              <div className="mt-4">
                <div className="text-5xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {benefit.icon}
                </div>
                <h3 className={`text-2xl font-bold ${textMain} mb-4 font-mono`}>{benefit.title}</h3>
                <p className={`${textSecondary} leading-relaxed`}>{benefit.description}</p>
              </div>

              {/* Hover Effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${isROPage ? 'from-blue-500/0 to-cyan-500/0 group-hover:from-blue-500/10 group-hover:to-cyan-500/10' : 'from-green-500/0 to-cyan-500/0 group-hover:from-green-500/10 group-hover:to-cyan-500/10'} rounded-xl transition-all duration-500 pointer-events-none`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Benefits;

